import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './SignaturePad.css';

/* ── A signature drawn with a finger, a stylus or a mouse ───────────────
   Ported from the contracts app's pad (the branches and templates round,
   D15), so a consent signed at the chair, in theatre or at the desk is
   the same ink as a contract signed by a director. Pointer events so every
   input feels right; midpoint quadratic smoothing so strokes look like ink
   rather than polylines; devicePixelRatio-aware so the export stays crisp;
   re-measured whenever it is shown, because a canvas sized while hidden
   has no box. Clear and Undo work on the stroke list. */

type Point = { x: number; y: number };
type Stroke = Point[];

export type SignaturePadHandle = {
  clear: () => void;
  undo: () => void;
  isEmpty: () => boolean;
  /** A cropped transparent PNG data URL of the ink, or null when empty. */
  toDataUrl: () => string | null;
};

export type SignaturePadProps = {
  /** Fires as the ink changes: true once a stroke stands. */
  onInkChange?: (hasInk: boolean) => void;
  label?: string;
  height?: number;
  disabled?: boolean;
  'data-testid'?: string;
};

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad({ onInkChange, label = 'Sign here', height = 150, disabled, 'data-testid': testId }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Stroke[]>([]);
  const current = useRef<Stroke | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const ratio = window.devicePixelRatio || 1;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111827';
    context.lineWidth = 2.4;
    const all = current.current ? [...strokes.current, current.current] : strokes.current;
    for (const stroke of all) {
      if (stroke.length < 2) {
        if (stroke.length === 1) {
          context.beginPath();
          context.arc(stroke[0].x, stroke[0].y, 1.2, 0, Math.PI * 2);
          context.fillStyle = '#111827';
          context.fill();
        }
        continue;
      }
      context.beginPath();
      context.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length - 1; i += 1) {
        const midX = (stroke[i].x + stroke[i + 1].x) / 2;
        const midY = (stroke[i].y + stroke[i + 1].y) / 2;
        context.quadraticCurveTo(stroke[i].x, stroke[i].y, midX, midY);
      }
      const last = stroke[stroke.length - 1];
      context.lineTo(last.x, last.y);
      context.stroke();
    }
  }, []);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    redraw();
  }, [redraw]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    const observer = typeof ResizeObserver !== 'undefined' && canvasRef.current ? new ResizeObserver(() => resize()) : null;
    if (observer && canvasRef.current) observer.observe(canvasRef.current);
    return () => {
      window.removeEventListener('resize', resize);
      observer?.disconnect();
    };
  }, [resize]);

  const setInk = (next: boolean) => {
    setHasInk(next);
    onInkChange?.(next);
  };

  const api: SignaturePadHandle = {
    clear: () => {
      strokes.current = [];
      current.current = null;
      redraw();
      setInk(false);
    },
    undo: () => {
      strokes.current = strokes.current.slice(0, -1);
      redraw();
      setInk(strokes.current.length > 0);
    },
    isEmpty: () => strokes.current.length === 0,
    toDataUrl: () => {
      const canvas = canvasRef.current;
      if (!canvas || strokes.current.length === 0) return null;
      const ratio = window.devicePixelRatio || 1;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const stroke of strokes.current) {
        for (const point of stroke) {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        }
      }
      const pad = 12;
      const sx = Math.max(0, (minX - pad) * ratio);
      const sy = Math.max(0, (minY - pad) * ratio);
      const sw = Math.min(canvas.width - sx, (maxX - minX + pad * 2) * ratio);
      const sh = Math.min(canvas.height - sy, (maxY - minY + pad * 2) * ratio);
      if (sw <= 0 || sh <= 0) return null;
      const out = document.createElement('canvas');
      out.width = Math.round(sw);
      out.height = Math.round(sh);
      out.getContext('2d')!.drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);
      return out.toDataURL('image/png');
    },
  };
  useImperativeHandle(ref, () => api);

  function pointFrom(event: React.PointerEvent<HTMLCanvasElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  return (
    <div className={`mrd-sigpad${disabled ? ' is-disabled' : ''}${hasInk ? ' has-ink' : ''}`} data-testid={testId}>
      <canvas
        ref={canvasRef}
        className="mrd-sigpad__canvas"
        style={{ height }}
        aria-label={label}
        role="img"
        onPointerDown={(event) => {
          if (disabled) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          current.current = [pointFrom(event)];
          redraw();
        }}
        onPointerMove={(event) => {
          if (!current.current) return;
          current.current.push(pointFrom(event));
          redraw();
        }}
        onPointerUp={() => {
          if (current.current) {
            strokes.current.push(current.current);
            current.current = null;
            redraw();
            setInk(true);
          }
        }}
        onPointerCancel={() => {
          current.current = null;
          redraw();
        }}
      />
      <div className="mrd-sigpad__bar">
        <span className="mrd-sigpad__hint">{hasInk ? 'Signed in the box' : label}</span>
        <span className="mrd-sigpad__acts">
          <button type="button" className="mrd-sigpad__act" disabled={disabled || !hasInk} onClick={api.undo} data-testid={testId ? `${testId}-undo` : undefined}>
            Undo
          </button>
          <button type="button" className="mrd-sigpad__act" disabled={disabled || !hasInk} onClick={api.clear} data-testid={testId ? `${testId}-clear` : undefined}>
            Clear
          </button>
        </span>
      </div>
    </div>
  );
});
